import lodashCompact from 'lodash/fp/compact';
import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
} from 'client/helpers/formikHelpers';

import SrCard, {
  SR_CARD_BULLET_COLOR_YELLOW,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';

const getFormFields = ({ leasingOperationsManagers, accountManagers }) => {
  return [
    {
      id: 'leasingOperationsManagerUserId',
      title: 'Lease Operation Manager (Required)',
      type: 'select',
      properties: {
        choices: lodashCompact([
          ...leasingOperationsManagers.map((lm) => ({
            label: lm.name,
            value: lm.id,
          })),
        ]),
      },
      validations: ['required'],
    },
    {
      id: 'accountManagerUserId',
      title: 'Account Manager',
      type: 'select',
      properties: {
        choices: lodashCompact([
          ...accountManagers.map((am) => ({
            label: am.name,
            value: am.id,
          })),
        ]),
      },
      validations: ['differentValue:leasingOperationsManagerUserId'],
    },
  ];
};

const SrPartnerSettingsAdminForm = ({
  partner,
  leasingOperationsManagers,
  accountManagers,
  onSubmit,
  inProgress,
}) => {

  const _getDefaultValues = () => {
    return {
      leasingOperationsManagerUserId: partner.leasingOperationsManagerUserId,
      accountManagerUserId: partner.accountManagerUserId || accountManagers[0].id,
    };
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({
        formFields: getFormFields({ leasingOperationsManagers, accountManagers }),
        defaultValues: _getDefaultValues(),
      })}
      validate={(values) => {
        const formFields = getFormFields({ leasingOperationsManagers, accountManagers });
        return createFormValidationFn(formFields)(values);
      }}
      render={(formikBag) => {
        const formFields = getFormFields({ leasingOperationsManagers, accountManagers });
        const getFormFieldsById = createFormFieldsByIdGetter(formFields);

        return (
          <>
            <Form>
              <SrCard
                color={SR_CARD_BULLET_COLOR_YELLOW}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                header='Assign Managers'
                subheader={(
                  'Here you can assign the Leasing Operations Manager, ' +
                  'and the Account Manager for this partner. ' +
                  'The Account Manager is optional.'
                )}
              >
                <SrGridRow
                  columns={2}
                >
                  <SrFormikField
                    formField={getFormFieldsById('leasingOperationsManagerUserId')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('accountManagerUserId')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </SrGridRow>
              </SrCard>
              <SrStaticFooter>
                <span />
                <SrFooterSaveButton isSubmitting={inProgress} />
              </SrStaticFooter>
            </Form>
          </>
        );
      }}
    />
  );
};

SrPartnerSettingsAdminForm.propTypes = {
  partner: PropTypes.object.isRequired,
  leasingOperationsManagers: PropTypes.array.isRequired,
  accountManagers: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
};

SrPartnerSettingsAdminForm.displayName = 'SrPartnerSettingsAdminForm';

export default SrPartnerSettingsAdminForm;
